import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ClipboardList,
  Map,
  FolderOpen,
  Users,
  LogOut,
  BookOpen,
  LifeBuoy,
  Contact,
  FileBarChart,
  Sparkles,
} from "lucide-react";
import { base44 } from "@/api/base44Client";
import { useAccessibility } from "@/lib/accessibilityContext";
import SupportRequestModal from "@/components/SupportRequestModal";
import { useAuth } from "@/lib/AuthContext";
import { ShieldCheck, Settings as SettingsIcon } from "lucide-react";
import { askDaiHref } from '@/components/askdai/navigation';

const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/assessment", label: "Assessment", icon: ClipboardList },
  { path: "/roadmap", label: "Roadmap", icon: Map },
  { path: "/evidence-vault", label: "Evidence Vault", icon: FolderOpen },
  { path: "/resources", label: "Resource Library", icon: BookOpen },
  { path: "/slt", label: "Leadership", icon: Users },
  { path: "support", label: "Consultancy & Training", icon: LifeBuoy },
  { path: "/personas", label: "Personas", icon: Contact },
  { path: "/esg-bids", label: "ESG & Bids", icon: FileBarChart },
];


export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const showSupport = new URLSearchParams(location.search).get('support') === 'training';
  const openSupport = () => {
    const params = new URLSearchParams(location.search); params.set('support', 'training');
    navigate(`${location.pathname}?${params}`, { state: { ...location.state, supportEntry: true } });
  };
  const closeSupport = () => {
    if (location.state?.supportEntry) navigate(-1);
    else { const params = new URLSearchParams(location.search); params.delete('support'); navigate(`${location.pathname}${params.size ? '?' + params : ''}`, { replace: true }); }
  };
  const { user } = useAuth();
  const items = [
    ...navItems,
    { path: "/settings", label: "Settings", icon: SettingsIcon },
    ...(user?.is_provider
      ? [{ path: "/admin", label: "DI Console", icon: ShieldCheck }]
      : []),
  ];
  const { prefs } = useAccessibility();
  const isLight = prefs.contrastMode === "standard";
  const currentLinkRef = React.useRef(null);

  React.useEffect(() => {
    const focusCurrent = () => currentLinkRef.current?.focus();
    window.addEventListener('inclusivecore:focus-main-menu', focusCurrent);
    return () => window.removeEventListener('inclusivecore:focus-main-menu', focusCurrent);
  }, []);

  return (
    <nav
      role="navigation"
      aria-label="Main navigation"
      className="navigation-focus-surface flex h-full w-16 shrink-0 flex-col bg-[#111111] text-white sm:w-64"
    >
      {/* Logo */}
      <div className="flex h-16 items-center border-b border-white/10 px-3 py-3 sm:px-6">
        <div className="flex items-center gap-2">
          <img
            data-decorative-image="true"
            src="/diverse-integration-logo.png"
            alt="Diverse Integration"
            className="h-10 w-10 object-contain"
          />
          <span className="text-only-alternative font-semibold">Diverse Integration</span>
        </div>
      </div>

      {/* Nav links */}
      <ul className="flex-1 px-3 py-4 space-y-1" role="list">
        <li><a href={askDaiHref(location)} onClick={(event) => { event.preventDefault(); window.dispatchEvent(new CustomEvent('inclusivecore:ask-dai-open', { detail: { invoker: event.currentTarget } })); }} className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-white/70 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FFB800] focus-visible:outline-offset-2"><Sparkles className="h-4 w-4 shrink-0" aria-hidden="true" /><span className="sr-only sm:not-sr-only">Ask Dai</span></a></li>
        {items.map(({ path, label, icon: Icon }) => {
          if (path === 'support') return <li key={path}><a href={`${location.pathname}?support=training`} onClick={event => { event.preventDefault(); openSupport(); }} className="flex min-h-11 items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FFB800]"><Icon className="h-4 w-4 shrink-0" aria-hidden="true" /><span className="sr-only sm:not-sr-only">{label}</span></a></li>;
          const active = location.pathname === path;
          return (
            <li key={path}>
              <Link
                ref={active ? currentLinkRef : undefined}
                to={path}
                onClick={() => window.dispatchEvent(new CustomEvent('inclusivecore:menu-route', { detail: { originKey: location.key } }))}
                aria-current={active ? "page" : undefined}
                className={`flex min-h-11 items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FFB800] focus-visible:outline-offset-2 ${
                  active
                    ? "bg-[#FFB800] text-black"
                    : "text-white/70 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
                <span className="sr-only sm:not-sr-only">{label}</span>
              </Link>
            </li>
          );
        })}

      </ul>

      {/* Footer */}
      <div className="px-3 py-3 border-t border-white/10">
        <button
          onClick={() => base44.auth.logout("/")}
          className="flex min-h-11 items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/60 hover:bg-white/10 hover:text-white w-full text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FFB800]"
        >
          <LogOut className="w-4 h-4 shrink-0" aria-hidden="true" />
          <span className="sr-only sm:not-sr-only">Sign out</span>
        </button>
      </div>

      {showSupport && (
        <SupportRequestModal onClose={closeSupport} />
      )}
    </nav>
  );
}
